/**
 * Estratégia de contemplação da venda (migration 237) — o que o cliente quer
 * para ser contemplado. Puro (sem React, sem banco) para ser a MESMA régua no
 * formulário de venda, na aba da venda, na Contemplação, na fila e no aviso.
 *
 *   nenhuma     não decidido — vale o "lance fixo automático" de sempre.
 *   sorteio     o cliente não quer lance: o robô não dá nenhum e o vendedor
 *               não tem ação de lance.
 *   lance_fixo  só fixo, nunca livre: o robô dá o fixo na véspera.
 *   lance_livre o cliente quer livre (o vendedor dá pelo site); o fixo
 *               automático fica como garantia, ligado ou desligado.
 *
 * O banco segura a coerência com `lance_fixo_automatico` (gatilho da 237):
 * sorteio → sempre false; lance_fixo → true quando escolhido. O robô espelha
 * estas regras em api/api-server/servopa/src/estrategia-contemplacao.mjs.
 *
 * Fidelidade anda junto com o fixo: valor definido pela Servopa, é o que o
 * automático oferta antes do fixo (--fidelidade-auto).
 */

export type EstrategiaContemplacao = 'nenhuma' | 'sorteio' | 'lance_fixo' | 'lance_livre';

export const ESTRATEGIAS: readonly EstrategiaContemplacao[] = [
  'nenhuma',
  'sorteio',
  'lance_fixo',
  'lance_livre',
];

export const ESTRATEGIA_LABEL: Record<EstrategiaContemplacao, string> = {
  nenhuma: 'Nenhuma',
  sorteio: 'Só sorteio',
  lance_fixo: 'Lance fixo',
  lance_livre: 'Lance livre',
};

/** Uma linha, para ir embaixo do campo. */
export const ESTRATEGIA_EXPLICACAO: Record<EstrategiaContemplacao, string> = {
  nenhuma: 'Ainda não combinado com o cliente. Vale o lance fixo automático abaixo.',
  sorteio: 'Cliente sem interesse em lance. Nenhum lance é registrado; a cota concorre apenas no sorteio.',
  lance_fixo: 'Cliente opta apenas pelo lance fixo, registrado automaticamente na véspera da assembleia.',
  lance_livre: 'Cliente opta pelo lance livre, registrado pelo vendedor no site. O lance fixo automático pode servir de garantia.',
};

/** Valor desconhecido (coluna ainda não migrada, lixo) vira 'nenhuma'. */
export function normalizarEstrategia(valor: unknown): EstrategiaContemplacao {
  return ESTRATEGIAS.includes(valor as EstrategiaContemplacao)
    ? (valor as EstrategiaContemplacao)
    : 'nenhuma';
}

export type TipoDeLance = 'F' | 'D' | 'L';

export type CaminhosPermitidos = {
  /** Toda cota ativa concorre no sorteio, com ou sem lance. */
  sorteio: true;
  fixo: boolean;
  fidelidade: boolean;
  livre: boolean;
};

/** Quais caminhos de contemplação a tela deve oferecer para esta estratégia. */
export function caminhosPermitidos(estrategia: unknown): CaminhosPermitidos {
  switch (normalizarEstrategia(estrategia)) {
    case 'sorteio':
      return { sorteio: true, fixo: false, fidelidade: false, livre: false };
    case 'lance_fixo':
      return { sorteio: true, fixo: true, fidelidade: true, livre: false };
    case 'lance_livre':
    case 'nenhuma':
    default:
      return { sorteio: true, fixo: true, fidelidade: true, livre: true };
  }
}

/** Os tipos de lance (códigos da Servopa) que o vendedor pode dar pelo site. */
export function tiposDeLancePermitidos(estrategia: unknown): TipoDeLance[] {
  const c = caminhosPermitidos(estrategia);
  const tipos: TipoDeLance[] = [];
  if (c.fixo) tipos.push('F');
  if (c.fidelidade) tipos.push('D');
  if (c.livre) tipos.push('L');
  return tipos;
}

/** O vendedor tem alguma ação de lance nesta venda? (sorteio: não.) */
export function temAcaoDeLance(estrategia: unknown): boolean {
  return tiposDeLancePermitidos(estrategia).length > 0;
}

/**
 * O controle "fixo automático" aparece? Só onde ele decide algo: em 'nenhuma'
 * (comportamento de hoje) e em 'lance_livre' (garantia). Sorteio e fixo
 * definem o flag sozinhos.
 */
export function mostraFixoAutomatico(estrategia: unknown): boolean {
  const e = normalizarEstrategia(estrategia);
  return e === 'nenhuma' || e === 'lance_livre';
}

/**
 * O valor de `lance_fixo_automatico` para gravar junto com a estratégia —
 * o mesmo que o gatilho da 237 faria, para a tela não piscar.
 */
export function lanceFixoAutomaticoPara(estrategia: unknown, atual: boolean | null | undefined): boolean {
  const e = normalizarEstrategia(estrategia);
  if (e === 'sorteio') return false;
  if (e === 'lance_fixo') return true;
  return atual !== false;
}

/**
 * O robô vai dar o fixo automático nesta cota? Null no flag conta como
 * ligado (o default da coluna), como no resto do app.
 */
export function fixoAutomaticoLigado(estrategia: unknown, lanceFixoAutomatico: boolean | null | undefined): boolean {
  if (normalizarEstrategia(estrategia) === 'sorteio') return false;
  return lanceFixoAutomatico !== false;
}

/** Sem lance vivo nesta assembleia: nenhum, falhou ou cancelado (régua da fila, migration 89). */
export function semLanceVivo(statusDoLance: string | null | undefined): boolean {
  return statusDoLance == null || statusDoLance === 'failed' || statusDoLance === 'cancelled';
}

/**
 * A cota pede ação do VENDEDOR para esta assembleia? Mesma regra da
 * seller_action_queue (237):
 *   - já tem lance vivo → não;
 *   - sorteio → nunca;
 *   - lance_fixo com o automático ligado → só se o robô não resolveu: a
 *     tentativa dele falhou/foi cancelada, ou chegou o dia
 *     (`diasAteAssembleia <= 0`) e ainda não há lance;
 *   - nenhuma / lance_livre → sim.
 *
 * `diasAteAssembleia` é opcional; sem ele, lance_fixo sem tentativa conta
 * como "o robô ainda vai dar".
 */
export function precisaAcaoDeLance(
  estrategia: unknown,
  lanceFixoAutomatico: boolean | null | undefined,
  statusDoLance: string | null | undefined,
  opts: { diasAteAssembleia?: number | null } = {},
): boolean {
  if (!semLanceVivo(statusDoLance)) return false;
  const e = normalizarEstrategia(estrategia);
  if (e === 'sorteio') return false;
  if (e === 'lance_fixo' && lanceFixoAutomatico !== false) {
    if (statusDoLance === 'failed' || statusDoLance === 'cancelled') return true;
    return opts.diasAteAssembleia != null && opts.diasAteAssembleia <= 0;
  }
  return true;
}
